import { normalizeAutomationTestId } from "../../components/InputField/testId";

export const LOGIN_V2_DECISION = {
  AUTO_RESUME: "AUTO_RESUME",
  AUTO_CREATE: "AUTO_CREATE",
  SHOW_CHOOSER: "SHOW_CHOOSER",
  BLOCKED: "BLOCKED",
};

export const AUTH_V2_FORM_TYPES = {
  BUSINESS: "BUSINESS",
  BUSINESS_PLUS: "BUSINESS_PLUS",
  BUSINESS_PRIME: "BUSINESS_PRIME",
  INDIVIDUAL_PLUS: "INDIVIDUAL_PLUS",
  INDIVIDUAL_PRIME: "INDIVIDUAL_PRIME",
};

const BUSINESS_TYPE_TO_CREATE_FORM_TYPE = {
  1: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
  2: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
  3: AUTH_V2_FORM_TYPES.BUSINESS_PRIME,
};

const BUSINESS_TYPE_TO_ROUTE_FORM_TYPE = {
  1: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
  2: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
  3: AUTH_V2_FORM_TYPES.BUSINESS_PRIME,
};

const FORM_TYPE_ALIASES = {
  INDIVIDUALPLUS: AUTH_V2_FORM_TYPES.INDIVIDUAL_PLUS,
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const isBlank = (value) => value === undefined || value === null || value === "";

export const normalizeFormType = (formType) => {
  const normalizedFormType = typeof formType === "string" ? formType.trim().toUpperCase() : "";
  return FORM_TYPE_ALIASES[normalizedFormType] || normalizedFormType;
};

export const normalizeApplicationType = (type) => {
  if (isBlank(type)) {
    return null;
  }

  const numericType = Number(type);
  return Number.isFinite(numericType) ? numericType : null;
};

export const toKebabCase = (value, fallback = "") => normalizeAutomationTestId(value, fallback);

const getBusinessTypeFromForm = (form = {}) => {
  const explicitType = normalizeApplicationType(form?.type ?? form?.defaultType);
  if (explicitType) {
    return explicitType;
  }

  if (form?.path === "business-prime-plus") {
    return 2;
  }

  if (form?.path === "business-prime" || normalizeFormType(form?.formType) === AUTH_V2_FORM_TYPES.BUSINESS_PRIME) {
    return 3;
  }

  return 1;
};

const isBusinessRouteForm = (form = {}) => {
  const formType = normalizeFormType(form?.formType);
  return (
    form?.apiPath === "business" ||
    form?.path === "business-plus" ||
    form?.path === "business-prime-plus" ||
    form?.path === "business-prime" ||
    form?.path === "business-v2" ||
    form?.path === "business-v2/upgrade" ||
    formType === AUTH_V2_FORM_TYPES.BUSINESS ||
    formType === AUTH_V2_FORM_TYPES.BUSINESS_PLUS ||
    formType === AUTH_V2_FORM_TYPES.BUSINESS_PRIME
  );
};

export const getCreateOptionLogicalKey = (option = {}) => {
  const normalizedOption = normalizeCreateOption(option);
  const formTypeKey = toKebabCase(normalizedOption.formType, "create-option");
  return normalizedOption.type === null
    ? formTypeKey
    : `${formTypeKey}-type-${normalizedOption.type}`;
};

export const getApplicationOptionLogicalKey = (application = {}) => {
  const normalizedApplication = normalizeApplicationOption(application);
  const formTypeKey = toKebabCase(
    normalizedApplication.applicationFormType || normalizedApplication.formType,
    "application"
  );
  const statusKey = isBlank(normalizedApplication.status)
    ? "status-unavailable"
    : `status-${toKebabCase(String(normalizedApplication.status), "unknown")}`;
  const typeSuffix = normalizedApplication.type === null ? "" : `-type-${normalizedApplication.type}`;
  return `${formTypeKey}${typeSuffix}-${statusKey}`;
};

export const normalizeCreateOption = (option = {}) => {
  const type = normalizeApplicationType(option?.type);
  return {
    ...option,
    formType: normalizeFormType(option?.formType),
    type,
    allowed: option?.allowed === true,
    blockedReason: option?.blockedReason || "",
    logicalKey: `${toKebabCase(normalizeFormType(option?.formType), "create-option")}${type === null ? "" : `-type-${type}`}`,
  };
};

export const normalizeApplicationOption = (application = {}) => {
  const applicationId = (
    application?.application_id ||
    application?.applicationId ||
    application?._id ||
    ""
  );
  const type = normalizeApplicationType(application?.type);

  return {
    ...application,
    application_id: applicationId,
    applicationId,
    formType: normalizeFormType(application?.formType),
    applicationFormType: normalizeFormType(application?.applicationFormType),
    type,
    statusDescription: application?.statusDescription || "",
    resumeAllowed: application?.resumeAllowed !== false,
    blockedReason: application?.blockedReason || "",
    logicalKey: `${toKebabCase(
      normalizeFormType(application?.applicationFormType || application?.formType),
      "application"
    )}${type === null ? "" : `-type-${type}`}`,
  };
};

export const getCurrentRouteCreateKey = (form = {}) => {
  const formType = normalizeFormType(form?.formType);

  if (!form || formType === "EDD" || form?.type === "kyc") {
    return null;
  }

  if (isBusinessRouteForm(form)) {
    const type = getBusinessTypeFromForm(form);
    const createFormType = BUSINESS_TYPE_TO_CREATE_FORM_TYPE[type] || AUTH_V2_FORM_TYPES.BUSINESS_PLUS;
    return getCreateOptionLogicalKey({ formType: createFormType, type });
  }

  if (formType) {
    return getCreateOptionLogicalKey({ formType });
  }

  return null;
};

export const doesCreateOptionMatchCurrentRoute = (option = {}, form = {}) => {
  const routeKey = getCurrentRouteCreateKey(form);
  return Boolean(routeKey) && getCreateOptionLogicalKey(option) === routeKey;
};

export const getAllowedCreateOptions = (createOptions = [], form = {}) => (
  toArray(createOptions)
    .map(normalizeCreateOption)
    .filter((option) => option.allowed && doesCreateOptionMatchCurrentRoute(option, form))
);

export const getRouteMismatchedAllowedCreateOptions = (createOptions = [], form = {}) => (
  toArray(createOptions)
    .map(normalizeCreateOption)
    .filter((option) => option.allowed && !doesCreateOptionMatchCurrentRoute(option, form))
);

export const getBlockedCreateOptions = (createOptions = []) => (
  toArray(createOptions)
    .map(normalizeCreateOption)
    .filter((option) => !option.allowed)
);

export const getApplicationOptions = (applications = []) => (
  toArray(applications)
    .map(normalizeApplicationOption)
    .filter((application) => Boolean(application.applicationId))
);

export const getResumableApplications = (applications = []) => (
  getApplicationOptions(applications).filter((application) => application.resumeAllowed)
);

export const getBlockedApplications = (applications = []) => (
  getApplicationOptions(applications).filter((application) => !application.resumeAllowed)
);

export const getUnifiedBusinessNavigationState = (type) => (
  Number(type) === 2 ? { businessV2ForceFirstStep: true } : undefined
);

export const resolveCreatePayloadForCurrentRoute = (form = {}, selectedOption = null) => {
  if (selectedOption) {
    const normalizedOption = normalizeCreateOption(selectedOption);
    return {
      formType: normalizedOption.formType,
      ...(normalizedOption.type !== null && { type: normalizedOption.type }),
    };
  }

  const formType = normalizeFormType(form?.formType);
  if (isBusinessRouteForm(form)) {
    const type = getBusinessTypeFromForm(form);
    return {
      formType: BUSINESS_TYPE_TO_CREATE_FORM_TYPE[type] || AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
      type,
    };
  }

  return formType ? { formType } : null;
};

export const resolveRouteInfoForApplication = (applicationOption = {}, fallbackForm = {}) => {
  const application = normalizeApplicationOption(applicationOption);
  const applicationId = application.applicationId;
  if (!applicationId) {
    return null;
  }

  const fallbackType = normalizeApplicationType(fallbackForm?.type ?? fallbackForm?.defaultType);
  const resolvedType = application.type ?? fallbackType;
  const formType = application.formType || normalizeFormType(fallbackForm?.formType);
  const applicationFormType = application.applicationFormType;
  const isBusinessApplication = [
    AUTH_V2_FORM_TYPES.BUSINESS,
    AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
    AUTH_V2_FORM_TYPES.BUSINESS_PRIME,
  ].includes(formType) || applicationFormType === AUTH_V2_FORM_TYPES.BUSINESS;

  if (isBusinessApplication) {
    if (resolvedType === 2) {
      return {
        url: `/business-prime-plus/${applicationId}`,
        state: getUnifiedBusinessNavigationState(resolvedType),
        formType: BUSINESS_TYPE_TO_ROUTE_FORM_TYPE[resolvedType],
      };
    }

    if (resolvedType === 3 || formType === AUTH_V2_FORM_TYPES.BUSINESS_PRIME) {
      return {
        url: `/business-prime/${applicationId}`,
        state: getUnifiedBusinessNavigationState(resolvedType),
        formType: AUTH_V2_FORM_TYPES.BUSINESS_PRIME,
      };
    }

    return {
      url: `/business-plus/${applicationId}`,
      state: getUnifiedBusinessNavigationState(resolvedType),
      formType: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
    };
  }

  if (formType === AUTH_V2_FORM_TYPES.INDIVIDUAL_PLUS) {
    return {
      url: `/individual-plus/${applicationId}`,
      state: undefined,
      formType,
    };
  }

  if (fallbackForm?.path) {
    return {
      url: `/${fallbackForm.path}/${applicationId}`,
      state: undefined,
      formType: formType || fallbackForm?.formType,
    };
  }

  return null;
};

export const buildLoginV2Decision = ({ applications = [], createOptions = [], form = {} } = {}) => {
  const applicationOptions = getApplicationOptions(applications);
  const resumableApplications = getResumableApplications(applications);
  const blockedApplications = getBlockedApplications(applications);
  const allowedCreateOptions = getAllowedCreateOptions(createOptions, form);
  const blockedCreateOptions = getBlockedCreateOptions(createOptions);
  const routeMismatchedAllowedCreateOptions = getRouteMismatchedAllowedCreateOptions(createOptions, form);

  const decisionBase = {
    applications: applicationOptions,
    resumableApplications,
    blockedApplications,
    allowedCreateOptions,
    blockedCreateOptions,
    routeMismatchedAllowedCreateOptions,
  };

  if (blockedApplications.length > 0 && allowedCreateOptions.length > 0) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.SHOW_CHOOSER,
    };
  }

  if (
    resumableApplications.length > 1 ||
    allowedCreateOptions.length > 1 ||
    (resumableApplications.length === 1 && allowedCreateOptions.length === 1) ||
    (applicationOptions.length > 1 && resumableApplications.length > 0)
  ) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.SHOW_CHOOSER,
    };
  }

  if (resumableApplications.length === 1) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.AUTO_RESUME,
      selectedApplication: resumableApplications[0],
    };
  }

  if (allowedCreateOptions.length === 1) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.AUTO_CREATE,
      selectedCreateOption: allowedCreateOptions[0],
    };
  }

  return {
    ...decisionBase,
    type: LOGIN_V2_DECISION.BLOCKED,
  };
};
